import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin", { replace: true });
    else setError(res.error || "Login failed");
  };

  return (
    <div className="grain min-h-screen bg-[#09090b] flex flex-col items-center justify-center px-4" data-testid="admin-login-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[50vw] h-[40vh] bg-rose-600/15 blur-[120px] rounded-full" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-zinc-500 hover:text-white transition-colors mb-8" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="bg-[#121216] border border-zinc-800 rounded-md p-8 sm:p-10">
          <div className="w-12 h-12 flex items-center justify-center bg-rose-600 rounded-md mb-6">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-anton uppercase text-3xl sm:text-4xl text-white leading-none">Admin Access</h1>
          <p className="mt-3 text-sm text-zinc-500">Zee Nation control room — enquiries &amp; content.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0c0c0f] border border-zinc-800 focus:border-rose-600 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
                placeholder="you@email.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0c0c0f] border border-zinc-800 focus:border-rose-600 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>

            {error && (
              <p className="text-sm text-rose-500 font-mono" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-sm transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
